(() => {
  const root = document.querySelector('[data-game]');
  if (!root) return;

  const canvas = root.querySelector('[data-game-canvas]');
  const foot = root.querySelector('[data-game-foot]');
  const ctx = canvas.getContext('2d');
  const els = {
    hi: root.querySelector('[data-game-hi]'),
    kicker: root.querySelector('[data-game-kicker]'),
    score: root.querySelector('[data-game-score]'),
    scoreWrap: root.querySelector('[data-game-score-wrap]'),
    progress: root.querySelector('[data-game-progress]'),
    rest: root.querySelector('[data-game-rest]'),
    start: root.querySelector('[data-game-start]'),
    restart: root.querySelector('[data-game-restart]'),
    continue: root.querySelector('[data-game-continue]'),
    resume: root.querySelector('[data-game-resume]'),
    showCode: root.querySelector('[data-game-show-code]'),
    sound: root.querySelector('[data-game-sound]'),
    fullscreen: root.querySelector('[data-game-fullscreen]'),
    win: root.querySelector('[data-game-win]'),
    winRunner: root.querySelector('[data-game-win-runner]'),
    code: root.querySelector('[data-game-code]'),
    copy: root.querySelector('[data-game-copy]'),
    live: root.querySelector('[data-game-live]')
  };

  const TARGET = Math.max(10, Number.parseInt(root.dataset.target, 10) || 500);
  // The section seals the code with a per-character SHA-256 shift; it is only reversed after a won run.
  const SEAL_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789-_.!$%&*+=?@/';
  const SEALED_CODE = root.dataset.seal || '';
  const unsealCode = async () => {
    const size = SEAL_ALPHABET.length;
    let code = '';
    for (const [index, char] of [...SEALED_CODE].entries()) {
      const position = SEAL_ALPHABET.indexOf(char);
      if (position < 0) {
        code += char;
        continue;
      }
      const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(`mxd:${TARGET}:${index}`));
      code += SEAL_ALPHABET[(position - (new Uint8Array(digest)[0] % size) + size) % size];
    }
    return code.trim();
  };

  const STORAGE_KEY = 'mixedown-game';
  const readStore = () => {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
    } catch (error) {
      return {};
    }
  };
  const writeStore = patch => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...readStore(), ...patch }));
    } catch (error) {}
  };

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  // Sprites are drawn as pixel art: every art pixel is PX world units, and a world unit is `scale` CSS pixels.
  const PX = 2;
  // The runner is a little hoodie in profile, hood up, big shiny eye and a smile, looking where it runs.
  const HOODIE_TOP = [
    '.....########',
    '...############',
    '..##############',
    '.#######.......##',
    '#######.........#',
    '######.....##...#',
    '######.....##...#',
    '######.....##...#',
    '######.......##.#',
    '.######.........#',
    '..######......###',
    '...#############',
    '....##########',
    '....##########',
    '....##########',
    '....###....###',
    '....##########',
    '.....########'
  ];
  const withRows = (rows, replace) => rows.map((row, index) => replace[index] || row);
  const overlay = (rows, extra) => rows.map((row, y) => row.replace(/./g, (char, x) => (extra[y] && extra[y][x] === '#' ? '#' : char)));
  const HOODIE_BLINK = { 5: '######..........#', 6: '######..........#', 7: '######.....##...#' };
  const HOODIE_DEAD = { 5: '######.....#.#..#', 6: '######......#...#', 7: '######.....#.#..#', 8: '######.......#..#' };
  const HOODIE_LEGS = {
    stand: ['.....##..##', '.....##..##', '.....###.###'],
    runA: ['.....##..##', '....##....##', '...###....###'],
    runB: ['.....##..##', '.....##.###', '.....###']
  };
  const CACTUS_SMALL = [
    '...##...',
    '..####..',
    '..####..',
    '..####.#',
    '#.####.#',
    '#.####.#',
    '#.####.#',
    '#.######',
    '#.####..',
    '######..',
    '..####..',
    '..####..',
    '..####..',
    '..####..',
    '..####..',
    '..####..',
    '..####..'
  ];
  const CACTUS_LARGE = [
    '.....##.....',
    '....####....',
    '....####....',
    '....####....',
    '.#..####..#.',
    '###.####.###',
    '###.####.###',
    '###.####.###',
    '###.####.###',
    '###.####.###',
    '###.####.###',
    '###.####.###',
    '###.########',
    '###.#######.',
    '########....',
    '.#######....',
    '....####....',
    '....####....',
    '....####....',
    '....####....',
    '....####....',
    '....####....',
    '....####....',
    '....####....',
    '....####....'
  ];
  const BIRD_BODY = [
    '....##.................',
    '...####................',
    '..##############.......',
    '#####################..',
    '....################...',
    '.......###########.....',
    '........######.........'
  ];
  const BIRD_UP = [
    '........#..............',
    '........##.............',
    '........###............',
    '........####...........',
    '........#####..........',
    '........######.........'
  ];
  const BIRD_DOWN = [
    '........#####..........',
    '........####...........',
    '........###............',
    '........##.............',
    '........#..............'
  ];
  const CLOUD = [
    '..........######........',
    '........###....##.......',
    '...######........###....',
    '.##...............####..',
    '#.....................##',
    '########################'
  ];
  const MOON = ['..####.', '.###...', '###....', '###....', '###....', '.###...', '..####.'];

  const makeSprite = rows => {
    const w = Math.max(...rows.map(row => row.length));
    const mask = rows.map(row => row.padEnd(w, '.'));
    return { w: w * PX, h: rows.length * PX, mask, cols: w, image: null };
  };

  const SPRITES = {
    runnerStand: makeSprite([...HOODIE_TOP, ...HOODIE_LEGS.stand]),
    runnerBlink: makeSprite([...withRows(HOODIE_TOP, HOODIE_BLINK), ...HOODIE_LEGS.stand]),
    runnerRunA: makeSprite([...HOODIE_TOP, ...HOODIE_LEGS.runA]),
    runnerRunB: makeSprite([...HOODIE_TOP, ...HOODIE_LEGS.runB]),
    runnerDead: makeSprite([...withRows(HOODIE_TOP, HOODIE_DEAD), ...HOODIE_LEGS.stand]),
    cactusSmall: makeSprite(CACTUS_SMALL),
    cactusLarge: makeSprite(CACTUS_LARGE),
    birdUp: makeSprite([...BIRD_UP, ...overlay(BIRD_BODY, ['........#######', '........########']), '', '', '', '', '']),
    birdDown: makeSprite(['', '', '', '', '', '', ...BIRD_BODY, ...BIRD_DOWN]),
    cloud: makeSprite(CLOUD),
    moon: makeSprite(MOON)
  };

  const isSolid = (sprite, x, y) => {
    const col = Math.floor(x / PX);
    const row = Math.floor(y / PX);
    return row >= 0 && row < sprite.mask.length && col >= 0 && col < sprite.cols && sprite.mask[row][col] === '#';
  };

  // Each art pixel is rounded to whole device pixels, so the sprites stay crisp at any scale.
  const renderSprite = (sprite, unit) => {
    const image = sprite.image || document.createElement('canvas');
    const edge = index => Math.round(index * unit);
    image.width = Math.max(1, edge(sprite.cols));
    image.height = Math.max(1, edge(sprite.mask.length));
    const g = image.getContext('2d');
    g.clearRect(0, 0, image.width, image.height);
    g.fillStyle = '#000';
    sprite.mask.forEach((row, y) => {
      for (let x = 0; x < sprite.cols; x += 1) {
        if (row[x] === '#') g.fillRect(edge(x), edge(y), edge(x + 1) - edge(x), edge(y + 1) - edge(y));
      }
    });
    sprite.image = image;
  };

  const GRAVITY = 0.6;
  const JUMP_VELOCITY = 10;
  const RELEASE_GRAVITY = 1.8;
  const START_SPEED = 5;
  const MAX_SPEED = 10;
  const ACCELERATION = 0.0006;
  const SCORE_PER_UNIT = 0.025;
  const GAP_COEFFICIENT = 0.9;
  const MAX_GAP_FACTOR = 1.5;
  const NIGHT_EVERY = 300;
  const NIGHT_FRAMES = 600;

  const OBSTACLES = [
    { type: 'cactusSmall', minGap: 120, minSpeed: 0, groupSpeed: 5.6, maxGroup: 3, lift: [0] },
    { type: 'cactusLarge', minGap: 120, minSpeed: 0, groupSpeed: 6.4, maxGroup: 2, lift: [0] },
    { type: 'bird', minGap: 150, minSpeed: 6.6, groupSpeed: Infinity, maxGroup: 1, lift: [6, 56] }
  ];

  let width = 0;
  let height = 0;
  let dpr = 1;
  let scale = 1;
  // Device pixels per world unit, and the length of one display frame in 60 fps frames.
  let k = 1;
  let frameDt = 1;
  let worldWidth = 0;
  let groundY = 0;
  let runnerX = 0;

  let state = 'start';
  let speed = START_SPEED;
  let distance = 0;
  let score = 0;
  let obstacles = [];
  let clouds = [];
  let stars = [];
  let groundOffset = 0;
  let skyOffset = 0;
  // Entrance: milliseconds since the page finished loading, or null while it is still loading.
  let introMs = null;
  const INTRO_END = 760;
  let night = 0;
  let nightTimer = 0;
  let nextNight = NIGHT_EVERY;
  let lastMilestone = 0;
  let overAt = 0;
  let winTimer = 0;
  // After the target, a run can go on endlessly and keeps getting faster.
  let endless = false;
  let speedAtWin = START_SPEED;

  const store = readStore();
  let best = Number(store.best) || 0;
  let muted = store.muted === undefined ? root.dataset.sound === 'off' : Boolean(store.muted);
  const hasWon = () => readStore().won === SEALED_CODE && SEALED_CODE !== '';

  const runner = {
    y: 0,
    vy: 0,
    jumping: false,
    held: false,
    frame: 0,
    blinkIn: 180,
    blinkFor: 0
  };

  const pad = value => String(Math.floor(value)).padStart(5, '0');

  // Sounds: small square-wave blips in the spirit of the original, only after the first tap or key press.
  let audio = null;
  const tone = (frequency, duration, delay = 0, volume = 0.04) => {
    if (muted) return;
    if (!audio) {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      if (!AudioContext) return;
      audio = new AudioContext();
    }
    if (audio.state === 'suspended') audio.resume();
    const start = audio.currentTime + delay;
    const oscillator = audio.createOscillator();
    const gain = audio.createGain();
    oscillator.type = 'square';
    oscillator.frequency.setValueAtTime(frequency, start);
    gain.gain.setValueAtTime(volume, start);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + duration);
    oscillator.connect(gain).connect(audio.destination);
    oscillator.start(start);
    oscillator.stop(start + duration + 0.02);
  };
  const sounds = {
    jump: () => tone(740, 0.07),
    point: () => {
      tone(988, 0.08);
      tone(1319, 0.14, 0.09);
    },
    hit: () => {
      tone(196, 0.09, 0, 0.06);
      tone(98, 0.24, 0.08, 0.06);
    },
    win: () => [523, 659, 784, 1047, 1319].forEach((frequency, index) => tone(frequency, index === 4 ? 0.4 : 0.11, index * 0.1))
  };

  const announce = text => {
    els.live.textContent = '';
    window.setTimeout(() => {
      els.live.textContent = text;
    }, 60);
  };

  const setState = next => {
    if (next !== 'start' && panels.start) panels.start.style.animation = '';
    state = next;
    root.dataset.state = next;
    const kicker = { playing: 'Punkte', over: 'Game over', paused: 'Pausiert', winning: 'Geschafft' }[next];
    if (kicker) els.kicker.textContent = kicker;
  };

  const updateScore = () => {
    els.score.textContent = String(score);
    els.progress.style.transform = `scaleX(${Math.min(score / TARGET, 1)})`;
  };

  const updateHi = () => {
    els.hi.textContent = `HI ${pad(best)}`;
    els.hi.hidden = best <= 0;
  };

  const panels = {
    start: foot.querySelector('.mxd-game__panel--start'),
    score: foot.querySelector('.mxd-game__panel--score'),
    over: foot.querySelector('.mxd-game__panel--over'),
    paused: foot.querySelector('.mxd-game__panel--paused')
  };
  const measureFoot = () => {
    const sizes = {};
    Object.entries(panels).forEach(([name, panel]) => {
      const display = panel.style.display;
      panel.style.display = 'flex';
      sizes[name] = panel.offsetHeight + (Number.parseFloat(getComputedStyle(panel).marginTop) || 0);
      panel.style.display = display;
    });
    return Math.max(sizes.start, sizes.score + sizes.over, sizes.score + sizes.paused);
  };

  // The canvas backing store matches the screen's device pixels exactly, so the browser never rescales it.
  let deviceSize = null;
  const resize = () => {
    const rect = canvas.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    width = rect.width;
    height = rect.height;
    const ratio = Math.min(window.devicePixelRatio || 1, 3);
    const exact = deviceSize && Math.abs(deviceSize.width - width * ratio) < 1 && Math.abs(deviceSize.height - height * ratio) < 1;
    canvas.width = exact ? deviceSize.width : Math.round(width * ratio);
    canvas.height = exact ? deviceSize.height : Math.round(height * ratio);
    dpr = canvas.width / width;
    const portrait = height > width;
    const short = !portrait && height < 520;
    scale = Math.min(Math.max(Math.min(width / (portrait ? 420 : 860), height / (short ? 340 : 300)), 0.62), 1.25);
    worldWidth = width / scale;
    // The horizon sits just above the tallest text block at the bottom, so text and game never overlap.
    const footReserve = measureFoot() + Number.parseFloat(getComputedStyle(foot).paddingBottom) + 32;
    const groundMax = height * (portrait ? 0.5 : short ? 0.48 : 0.6);
    const groundPx = Math.round(Math.max(Math.min(height - footReserve, groundMax), height * 0.3));
    groundY = groundPx / scale;
    runnerX = (Number.parseFloat(getComputedStyle(foot).paddingLeft) || 25) / scale;
    k = scale * dpr;
    Object.values(SPRITES).forEach(sprite => renderSprite(sprite, PX * k));
    if (!stars.length || stars.worldWidth !== Math.round(worldWidth)) {
      stars = Array.from({ length: Math.round(worldWidth / 90) }, () => ({ x: Math.random() * worldWidth, y: 10 + Math.random() * (groundY - 90) }));
      stars.worldWidth = Math.round(worldWidth);
    }
    draw();
    fitCode();
  };

  const runnerSprite = () => {
    if (state === 'over') return SPRITES.runnerDead;
    if (state === 'start' || state === 'paused' || (state === 'winning' && !runner.jumping && winTimer > 40)) {
      return runner.blinkFor > 0 ? SPRITES.runnerBlink : SPRITES.runnerStand;
    }
    if (runner.jumping) return SPRITES.runnerStand;
    const step = Math.floor(runner.frame / 5) % 2;
    return step ? SPRITES.runnerRunB : SPRITES.runnerRunA;
  };

  const runnerBox = () => {
    const sprite = runnerSprite();
    return { sprite, x: runnerX, y: groundY - sprite.h - runner.y + PX };
  };

  const obstacleSprite = obstacle => {
    if (obstacle.type !== 'bird') return SPRITES[obstacle.type];
    return Math.floor(obstacle.frame / 10) % 2 ? SPRITES.birdDown : SPRITES.birdUp;
  };

  const spawnObstacle = () => {
    const pool = OBSTACLES.filter(entry => speed >= entry.minSpeed);
    const recent = obstacles.slice(-2).map(obstacle => obstacle.type);
    let candidates = pool.filter(entry => !(recent.length === 2 && recent[0] === entry.type && recent[1] === entry.type));
    if (!candidates.length) candidates = pool;
    const entry = candidates[Math.floor(Math.random() * candidates.length)];
    const sprite = entry.type === 'bird' ? SPRITES.birdUp : SPRITES[entry.type];
    const size = speed >= entry.groupSpeed ? 1 + Math.floor(Math.random() * entry.maxGroup) : 1;
    const lift = entry.lift[Math.floor(Math.random() * entry.lift.length)];
    const obstacleWidth = sprite.w * size;
    const minGap = Math.round(obstacleWidth * speed + entry.minGap * GAP_COEFFICIENT);
    const gap = minGap + Math.random() * (minGap * MAX_GAP_FACTOR - minGap);
    obstacles.push({
      type: entry.type,
      x: worldWidth + 10,
      size,
      lift,
      width: obstacleWidth,
      gap,
      frame: 0,
      speedOffset: entry.type === 'bird' ? (Math.random() < 0.5 ? -0.8 : 0.8) : 0
    });
  };

  // Pixel-accurate hit test: only touching ink counts, so close calls feel fair.
  const hits = obstacle => {
    const { sprite: runnerArt, x: dx, y: dy } = runnerBox();
    const art = obstacleSprite(obstacle);
    const oy = groundY - art.h - obstacle.lift + PX;
    for (let index = 0; index < obstacle.size; index += 1) {
      const ox = obstacle.x + index * art.w;
      const left = Math.max(dx, ox);
      const right = Math.min(dx + runnerArt.w, ox + art.w);
      const top = Math.max(dy, oy);
      const bottom = Math.min(dy + runnerArt.h, oy + art.h);
      if (left >= right || top >= bottom) continue;
      for (let y = top + 0.5; y < bottom; y += 1) {
        for (let x = left + 0.5; x < right; x += 1) {
          if (isSolid(runnerArt, x - dx, y - dy) && isSolid(art, x - ox, y - oy)) return true;
        }
      }
    }
    return false;
  };

  const resetRun = () => {
    speed = START_SPEED;
    distance = 0;
    score = 0;
    obstacles = [];
    groundOffset = 0;
    skyOffset = 0;
    nightTimer = 0;
    nextNight = NIGHT_EVERY;
    lastMilestone = 0;
    winTimer = 0;
    endless = false;
    root.classList.remove('is-endless', 'can-continue');
    Object.assign(runner, { y: 0, vy: 0, jumping: false, frame: 0 });
    root.classList.remove('is-night');
    updateScore();
  };

  const jump = () => {
    if (runner.jumping) return;
    runner.jumping = true;
    runner.vy = JUMP_VELOCITY + speed / 10;
    sounds.jump();
  };

  const startGame = () => {
    if (state === 'winning') return;
    introMs = INTRO_END;
    closeWin(false);
    resetRun();
    setState('playing');
    runner.held = true;
    jump();
    canvas.focus({ preventScroll: true });
  };

  const gameOver = () => {
    setState('over');
    overAt = performance.now();
    sounds.hit();
    if (navigator.vibrate) navigator.vibrate(60);
    const record = score > best;
    if (record) {
      best = score;
      writeStore({ best });
      updateHi();
    }
    const rest = TARGET - score;
    els.rest.textContent = endless
      ? `${record ? 'Neuer Rekord. ' : ''}Dein Code bleibt freigeschaltet.`
      : `${record ? 'Neuer Rekord. ' : ''}Noch ${rest} ${rest === 1 ? 'Punkt' : 'Punkte'} bis zu deinem Code.`;
    announce(`Game over bei ${score} Punkten. ${els.rest.textContent}`);
  };

  const reachTarget = () => {
    setState('winning');
    winTimer = 0;
    speedAtWin = speed;
    root.classList.add('can-continue');
    root.classList.remove('is-night');
    if (score > best) {
      best = score;
      updateHi();
    }
    writeStore({ best, won: SEALED_CODE });
    sounds.win();
  };

  // Weiterspielen: the run picks up at the target score on a clear track and never stops getting faster.
  const continueRun = () => {
    if (state !== 'winning') return;
    endless = true;
    root.classList.remove('can-continue');
    root.classList.add('is-endless');
    closeWin(false);
    obstacles = [];
    speed = speedAtWin;
    runner.held = false;
    setState('playing');
    canvas.focus({ preventScroll: true });
  };

  const pause = () => {
    if (state !== 'playing') return;
    setState('paused');
    announce('Pausiert. Tippe oder drücke die Leertaste zum Weiterspielen.');
  };

  const resume = () => {
    if (state !== 'paused') return;
    setState('playing');
    runner.held = false;
    canvas.focus({ preventScroll: true });
  };

  // Every moving layer advances a whole number of device pixels per display frame, so the scroll is perfectly
  // even instead of alternating between neighbouring pixel steps. The speed it rounds to differs by under a pixel.
  const even = unitsPerFrame => Math.round(unitsPerFrame * k * frameDt) / (k * frameDt);

  const step = dt => {
    if (state === 'start' || state === 'paused') {
      if (!introDone()) return;
      runner.blinkIn -= dt;
      if (runner.blinkFor > 0) runner.blinkFor -= dt;
      if (runner.blinkIn <= 0) {
        runner.blinkFor = 8;
        runner.blinkIn = 120 + Math.random() * 240;
      }
      return;
    }
    if (state === 'over') return;

    const winning = state === 'winning';
    if (winning) {
      winTimer += dt;
      speed = Math.max(0, speed - 0.12 * dt);
      if (!runner.jumping && winTimer > 40 && winTimer < 46) {
        runner.jumping = true;
        runner.vy = JUMP_VELOCITY;
      }
      if (runner.blinkFor > 0) runner.blinkFor -= dt;
      if (winTimer > 90 && !root.classList.contains('is-win-open')) openWin(true);
    } else {
      speed = endless ? speed + ACCELERATION * 2 * dt : Math.min(MAX_SPEED, speed + ACCELERATION * dt);
    }

    // Jump arc: holding makes it higher, letting go early pulls it down sooner on a smooth curve.
    // Exact per-step integration keeps the arc the same at any frame rate.
    if (runner.jumping) {
      const gravity = !runner.held && !winning && runner.vy > 0 ? GRAVITY * RELEASE_GRAVITY : GRAVITY;
      runner.y += runner.vy * dt - 0.5 * gravity * dt * dt;
      runner.vy -= gravity * dt;
      if (runner.y <= 0) {
        runner.y = 0;
        runner.vy = 0;
        runner.jumping = false;
      }
    }
    runner.frame += dt * (speed > 0 ? 1 : 0);

    const moved = even(speed) * dt;
    distance += moved;
    groundOffset += moved;
    skyOffset += even(speed * 0.05) * dt;

    for (let index = obstacles.length - 1; index >= 0; index -= 1) {
      const obstacle = obstacles[index];
      obstacle.x -= (obstacle.speedOffset && speed > 0 ? even(speed + obstacle.speedOffset) : even(speed)) * dt;
      obstacle.frame += dt;
      if (obstacle.x + obstacle.width <= -20) obstacles.splice(index, 1);
    }

    if (!winning) {
      const last = obstacles[obstacles.length - 1];
      if (!last || last.x + last.width + last.gap < worldWidth) spawnObstacle();
    }

    const drift = even(speed * 0.2) * dt;
    for (let index = clouds.length - 1; index >= 0; index -= 1) {
      clouds[index].x -= drift;
      if (clouds[index].x + SPRITES.cloud.w <= 0) clouds.splice(index, 1);
    }
    const lastCloud = clouds[clouds.length - 1];
    if (clouds.length < 6 && (!lastCloud || worldWidth - lastCloud.x > lastCloud.gap)) {
      clouds.push({ x: worldWidth + Math.random() * 40, y: 14 + Math.random() * Math.max(groundY - 120, 20), gap: 120 + Math.random() * 260 });
    }

    if (winning) return;

    const nextScore = Math.floor(distance * SCORE_PER_UNIT);
    if (nextScore !== score) {
      score = endless ? nextScore : Math.min(nextScore, TARGET);
      updateScore();
      const milestone = Math.floor(score / 100) * 100;
      if (milestone > lastMilestone && (endless || score < TARGET)) {
        lastMilestone = milestone;
        sounds.point();
        els.scoreWrap.classList.remove('is-milestone');
        void els.scoreWrap.offsetWidth;
        els.scoreWrap.classList.add('is-milestone');
      }
    }

    if (score >= nextNight && (endless || score < TARGET)) {
      nextNight += NIGHT_EVERY;
      nightTimer = NIGHT_FRAMES;
      root.classList.add('is-night');
    }
    if (nightTimer > 0) {
      nightTimer -= dt;
      if (nightTimer <= 0) root.classList.remove('is-night');
    }

    if (!endless && score >= TARGET) {
      reachTarget();
      return;
    }
    if (obstacles.some(hits)) gameOver();
  };

  const clamp01 = value => Math.min(Math.max(value, 0), 1);
  const easeOut = value => 1 - (1 - value) ** 4;
  const introDone = () => introMs !== null && introMs >= INTRO_END;
  // Where the entrance stands at `from`..`to` milliseconds, from 0 to 1.
  const intro = (from, to) => (introMs === null ? 0 : clamp01((introMs - from) / (to - from)));

  // The runner drops onto the horizon after it has drawn in, lands with a little hop and starts blinking.
  const introRunnerY = () => {
    const fall = intro(160, 460);
    if (fall < 1) return 90 * (1 - fall * fall);
    return 8 * Math.sin(Math.PI * intro(460, INTRO_END));
  };

  const draw = () => {
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    const sprite = (art, x, y) => ctx.drawImage(art.image, Math.round(x * k), Math.round(y * k));
    // Ground and cacti scroll by one shared whole-pixel shift, so they move in lockstep without shimmering edges.
    const shift = Math.round(groundOffset * k);
    const isNight = root.classList.contains('is-night');
    night += ((isNight ? 1 : 0) - night) * 0.05;

    if (night > 0.02) {
      ctx.globalAlpha = night;
      ctx.fillStyle = '#000';
      stars.forEach(star => {
        const x = (((star.x - skyOffset) % worldWidth) + worldWidth) % worldWidth;
        ctx.fillRect(Math.round(x * k), Math.round(star.y * k), Math.max(1, Math.round(PX * k)), Math.max(1, Math.round(PX * k)));
      });
      sprite(SPRITES.moon, worldWidth * 0.72, Math.max(groundY - 160, 16));
      ctx.globalAlpha = 1;
    }

    ctx.globalAlpha = 0.2 * easeOut(intro(260, INTRO_END));
    clouds.forEach(cloud => sprite(SPRITES.cloud, cloud.x, cloud.y));
    ctx.globalAlpha = 1;

    // Horizon with a little texture of pebbles and bumps that scrolls along.
    ctx.fillStyle = '#000';
    const line = Math.max(1, Math.round(k));
    const groundPx = Math.round(groundY * k);
    // On arrival the horizon draws itself in from the left.
    const reveal = Math.round(canvas.width * easeOut(intro(0, 520)));
    ctx.fillRect(0, groundPx, reveal, line);
    const cell = 6;
    const first = Math.floor(groundOffset / cell);
    const count = Math.ceil(worldWidth / cell) + 2;
    for (let index = 0; index < count; index += 1) {
      const n = first + index;
      const hash = Math.abs(Math.sin(n * 12.9898) * 43758.5453) % 1;
      const x = Math.round(n * cell * k) - shift;
      if (x > reveal) break;
      if (hash < 0.16) {
        const depth = 3 + Math.floor(hash * 40) % 4 * 2;
        ctx.fillRect(x, groundPx + Math.round(depth * k), Math.round((hash < 0.06 ? 4 : 2) * k), line);
      } else if (hash > 0.975) {
        ctx.fillRect(x, groundPx - line, Math.round(4 * k), line);
      }
    }

    obstacles.forEach(obstacle => {
      const art = obstacleSprite(obstacle);
      const y = Math.round((groundY - art.h - obstacle.lift + PX) * k);
      for (let index = 0; index < obstacle.size; index += 1) {
        const x = obstacle.x + index * art.w;
        const left = obstacle.speedOffset ? Math.round(x * k) : Math.round((x + groundOffset) * k) - shift;
        ctx.drawImage(art.image, left, y);
      }
    });

    if (intro(160, 161) > 0) {
      const { sprite: art, x, y } = runnerBox();
      sprite(art, x, y);
    }
  };

  // Physics runs in 60 fps frames, split into small steps, so the game feels the same on every display.
  // Frames arrive at the display's refresh rate, but their timestamps jitter. Each frame therefore advances by a
  // whole number of refresh intervals (the median of recent frame times): one normally, more after a dropped frame.
  const FRAME_MS = 1000 / 60;
  const frameTimes = [];
  let frameCount = 0;
  let refreshMs = FRAME_MS;
  let lastCallback = performance.now();
  let last = lastCallback;
  const loop = now => {
    requestAnimationFrame(loop);
    const interval = now - lastCallback;
    lastCallback = now;
    if (interval > 2 && interval < 100) {
      frameTimes.push(interval);
      if (frameTimes.length > 30) frameTimes.shift();
      frameCount += 1;
      if (frameTimes.length >= 10 && frameCount % 10 === 0) {
        const sorted = frameTimes.slice().sort((a, b) => a - b);
        refreshMs = sorted[sorted.length >> 1];
      }
    }
    const frames = Math.round((now - last) / refreshMs);
    if (frames < 1) return;
    last = now;
    frameDt = refreshMs / FRAME_MS;
    if (introMs !== null && !introDone()) {
      introMs = Math.min(introMs + frames * refreshMs, INTRO_END);
      if (state === 'start') runner.y = introDone() ? 0 : introRunnerY();
    }
    let elapsed = Math.min(frames * frameDt, 4);
    while (elapsed > 0) {
      const dt = Math.min(elapsed, 1);
      step(dt);
      elapsed -= dt;
    }
    draw();
  };

  const revealFrom = (x, y) => {
    els.win.style.setProperty('--reveal-x', `${Math.round(x)}px`);
    els.win.style.setProperty('--reveal-y', `${Math.round(y)}px`);
  };

  // The code stays on one line: a code too long for the box shrinks until it fits.
  const fitCode = () => {
    const code = els.code;
    if (els.win.hidden || !code.textContent) return;
    code.style.fontSize = '';
    code.style.whiteSpace = 'nowrap';
    const style = getComputedStyle(code);
    const room = code.clientWidth - Number.parseFloat(style.paddingLeft) - Number.parseFloat(style.paddingRight);
    const range = document.createRange();
    range.selectNodeContents(code);
    const text = range.getBoundingClientRect().width;
    if (text > room) code.style.fontSize = `${Math.max(16, Math.floor((Number.parseFloat(style.fontSize) * room) / text))}px`;
    code.style.whiteSpace = '';
  };

  const openWin = fromGame => {
    els.code.textContent = '';
    unsealCode()
      .catch(() => '')
      .then(code => {
        els.code.textContent = code;
        fitCode();
        announce(code ? `Geschafft. Dein Rabattcode lautet ${code.split('').join(' ')}.` : 'Geschafft.');
      });
    if (fromGame) {
      const { sprite: art, x, y } = runnerBox();
      revealFrom((x + art.w / 2) * scale, (y + art.h / 2) * scale);
    }
    els.win.hidden = false;
    els.win.getBoundingClientRect();
    root.classList.add('is-win-open');
    els.win.setAttribute('aria-hidden', 'false');
    window.setTimeout(() => els.win.focus({ preventScroll: true }), reducedMotion.matches ? 0 : 500);
  };

  const closeWin = focusStart => {
    if (!root.classList.contains('is-win-open')) return;
    root.classList.remove('is-win-open');
    els.win.setAttribute('aria-hidden', 'true');
    const hide = () => {
      if (!root.classList.contains('is-win-open')) els.win.hidden = true;
    };
    window.setTimeout(hide, reducedMotion.matches ? 0 : 720);
    if (focusStart) els.start.focus({ preventScroll: true });
  };

  const backToStart = () => {
    resetRun();
    setState('start');
    els.showCode.hidden = !hasWon();
    closeWin(true);
  };

  const copyResetLabel = els.copy ? els.copy.querySelector('[data-game-copy-label]') : null;
  let copyTimer = 0;
  const copyCode = () => {
    const code = els.code.textContent.trim();
    if (!code) return;
    const fallback = () =>
      new Promise((resolve, reject) => {
        const field = document.createElement('textarea');
        field.value = code;
        field.setAttribute('readonly', '');
        field.style.position = 'fixed';
        field.style.opacity = '0';
        document.body.appendChild(field);
        field.select();
        const copied = document.execCommand('copy');
        field.remove();
        if (copied) resolve();
        else reject();
      });
    const write = navigator.clipboard && window.isSecureContext ? navigator.clipboard.writeText(code).catch(fallback) : fallback();
    write
      .then(() => {
        els.copy.classList.add('is-copied');
        copyResetLabel.textContent = els.copy.dataset.copiedLabel;
        announce('Code kopiert.');
        window.clearTimeout(copyTimer);
        copyTimer = window.setTimeout(() => {
          els.copy.classList.remove('is-copied');
          copyResetLabel.textContent = els.copy.dataset.copyLabel;
        }, 2200);
      })
      .catch(() => {
        const selection = window.getSelection();
        const range = document.createRange();
        range.selectNodeContents(els.code);
        selection.removeAllRanges();
        selection.addRange(range);
      });
  };

  // Input: one action everywhere. Tap, click or space jumps.
  const press = () => {
    if (state === 'start') startGame();
    else if (state === 'playing') {
      runner.held = true;
      jump();
    } else if (state === 'over') {
      if (performance.now() - overAt > 450) startGame();
    } else if (state === 'paused') resume();
  };
  const release = () => {
    runner.held = false;
  };

  const isControl = target => target.closest('button, a, input, textarea, select, [data-game-win]');

  document.addEventListener('keydown', event => {
    if (event.metaKey || event.ctrlKey || event.altKey) return;
    if (root.classList.contains('is-win-open')) {
      if (event.key === 'Escape') backToStart();
      return;
    }
    const key = event.code;
    if (key === 'Space') {
      if (isControl(event.target)) return;
      event.preventDefault();
      if (!event.repeat) press();
    } else if (key === 'Enter' && !isControl(event.target) && state !== 'playing') {
      event.preventDefault();
      press();
    } else if ((key === 'Escape' || key === 'KeyP') && state === 'playing') {
      pause();
    }
  });
  document.addEventListener('keyup', event => {
    const key = event.code;
    if (key === 'Space') release();
  });

  let pointer = null;
  root.addEventListener('pointerdown', event => {
    if (isControl(event.target) || (event.pointerType === 'mouse' && event.button !== 0)) return;
    event.preventDefault();
    pointer = event.pointerId;
    press();
  });
  const endPointer = event => {
    if (event.pointerId !== pointer) return;
    pointer = null;
    release();
  };
  root.addEventListener('pointerup', endPointer);
  root.addEventListener('pointercancel', endPointer);
  root.addEventListener('contextmenu', event => {
    if (!isControl(event.target)) event.preventDefault();
  });

  els.start.addEventListener('click', startGame);
  els.restart.addEventListener('click', startGame);
  els.continue.addEventListener('click', continueRun);
  els.resume.addEventListener('click', resume);
  if (els.copy) els.copy.addEventListener('click', copyCode);
  els.showCode.addEventListener('click', () => {
    const rect = els.showCode.getBoundingClientRect();
    const box = root.getBoundingClientRect();
    revealFrom(rect.left - box.left + rect.width / 2, rect.top - box.top + rect.height / 2);
    openWin(false);
  });

  const setMuted = value => {
    muted = value;
    els.sound.setAttribute('aria-pressed', String(!muted));
    els.sound.setAttribute('aria-label', muted ? 'Ton einschalten' : 'Ton ausschalten');
    writeStore({ muted });
  };
  els.sound.addEventListener('click', () => setMuted(!muted));
  setMuted(muted);

  const fullscreenElement = () => document.fullscreenElement || document.webkitFullscreenElement;
  if (document.fullscreenEnabled || document.webkitFullscreenEnabled) {
    els.fullscreen.hidden = false;
    els.fullscreen.addEventListener('click', () => {
      if (fullscreenElement()) (document.exitFullscreen || document.webkitExitFullscreen).call(document);
      else {
        const target = document.documentElement;
        const request = target.requestFullscreen || target.webkitRequestFullscreen;
        const result = request.call(target);
        if (result && result.catch) result.catch(() => {});
      }
    });
    const syncFullscreen = () => {
      const active = Boolean(fullscreenElement());
      els.fullscreen.setAttribute('aria-pressed', String(active));
      els.fullscreen.setAttribute('aria-label', active ? 'Vollbild verlassen' : 'Vollbild');
    };
    document.addEventListener('fullscreenchange', syncFullscreen);
    document.addEventListener('webkitfullscreenchange', syncFullscreen);
  }

  document.addEventListener('visibilitychange', () => {
    if (document.hidden) pause();
  });
  window.addEventListener('blur', pause);

  if (els.winRunner) {
    const art = SPRITES.runnerStand;
    const unit = 3 * (window.devicePixelRatio > 1 ? 2 : 1);
    els.winRunner.width = art.cols * unit;
    els.winRunner.height = art.mask.length * unit;
    const g = els.winRunner.getContext('2d');
    g.fillStyle = '#000';
    art.mask.forEach((row, y) => {
      for (let x = 0; x < art.cols; x += 1) if (row[x] === '#') g.fillRect(x * unit, y * unit, unit, unit);
    });
  }

  const observer = new ResizeObserver(entries => {
    const box = entries[0].devicePixelContentBoxSize && entries[0].devicePixelContentBoxSize[0];
    deviceSize = box ? { width: box.inlineSize, height: box.blockSize } : null;
    resize();
  });
  try {
    observer.observe(canvas, { box: 'device-pixel-content-box' });
  } catch (error) {
    observer.observe(canvas);
  }
  if (document.fonts) document.fonts.ready.then(resize);
  resize();
  updateHi();
  updateScore();
  els.showCode.hidden = !hasWon();
  setState('start');
  requestAnimationFrame(now => {
    last = now;
    loop(now);
  });

  // The page reveals itself once the fonts have loaded, so nothing shifts while it animates in.
  let booted = false;
  const boot = () => {
    if (booted) return;
    booted = true;
    resize();
    if (introMs === null) introMs = reducedMotion.matches ? INTRO_END : 0;
    root.classList.remove('is-booting');
    root.classList.add('is-entering');
    window.setTimeout(() => {
      // The start panel must not replay its own rise when the entrance class comes off.
      if (state === 'start') panels.start.style.animation = 'none';
      root.classList.remove('is-entering');
    }, 1100);
  };
  if (document.fonts) document.fonts.ready.then(() => requestAnimationFrame(boot));
  window.setTimeout(boot, 700);
})();
